import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import {
  useCustomerQuoteContractSummary,
  QUOTE_STATUS_LABELS,
  CONTRACT_STATUS_LABELS,
} from '@/hooks/useQuoteContract';
import { canSeeQuoteContractMenu } from '@/lib/quoteContractPermissions';

interface Props {
  customerId: string;
}

export default function CustomerQuoteContractPanel({ customerId }: Props) {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const canRead = canSeeQuoteContractMenu(permissions);
  const { summary, loading, error, fetchSummary } =
    useCustomerQuoteContractSummary();

  useEffect(() => {
    if (canRead && customerId) void fetchSummary(customerId);
  }, [canRead, customerId, fetchSummary]);

  if (!canRead) return null;

  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">最新报价 / 合同</h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            navigate(
              `/quote-contract/quotes?customerId=${encodeURIComponent(customerId)}`,
            )
          }
        >
          查看全部报价
        </Button>
      </div>
      {loading && <p className="text-xs text-muted-foreground">加载中…</p>}
      {error && (
        <p className="text-xs text-destructive">加载失败：{error.message}</p>
      )}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-md border p-3">
          <div className="text-xs font-medium">最新已审核报价</div>
          {summary?.latestApprovedQuote ? (
            <div className="mt-1 space-y-1 text-xs">
              <div className="font-mono">
                {summary.latestApprovedQuote.quoteNo}
              </div>
              <div>{summary.latestApprovedQuote.title}</div>
              <div className="text-muted-foreground">
                {QUOTE_STATUS_LABELS[summary.latestApprovedQuote.status]} ·{' '}
                {summary.latestApprovedQuote.currency}{' '}
                {summary.latestApprovedQuote.totalAmount}
              </div>
              <button
                className="text-xs text-primary hover:underline"
                onClick={() =>
                  navigate(
                    `/quote-contract/quotes/${summary.latestApprovedQuote!.id}`,
                  )
                }
              >
                打开报价 →
              </button>
            </div>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">
              暂无已审核通过的报价
            </p>
          )}
        </div>
        <div className="rounded-md border p-3">
          <div className="text-xs font-medium">最新合同</div>
          {summary?.latestContract ? (
            <div className="mt-1 space-y-1 text-xs">
              <div className="font-mono">{summary.latestContract.contractNo}</div>
              <div>{summary.latestContract.title}</div>
              <div className="text-muted-foreground">
                {CONTRACT_STATUS_LABELS[summary.latestContract.status]} ·{' '}
                {summary.latestContract.currency}{' '}
                {summary.latestContract.effectiveTotalAmount}
              </div>
              <button
                className="text-xs text-primary hover:underline"
                onClick={() =>
                  navigate(
                    `/quote-contract/contracts/${summary.latestContract!.id}`,
                  )
                }
              >
                打开合同 →
              </button>
            </div>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">暂无合同</p>
          )}
        </div>
      </div>
    </Card>
  );
}
